import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquareQuote, Save, AlertCircle, Hourglass, Ban } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Catchphrase setter on the Dashboard. Players type a < 50-char phrase.
 * Backend auto-blocks profanity (instant rejection + strike), routes
 * suspicious phrases (URLs / spam / leet-speak) to admin queue, and lets
 * clean phrases through immediately.
 *
 * UI states: idle | submitting | clean | pending-review | rejected | banned.
 */
export default function CatchphraseCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/profile/catchphrase/me`, {
        withCredentials: true,
      });
      setData(r.data);
      setDraft(r.data?.catchphrase || r.data?.pending || "");
    } catch (_e) { /* silent */ }
  };

  const handleSave = async () => {
    if (!draft.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/profile/catchphrase`,
        { text: draft },
        { withCredentials: true },
      );
      if (r.data.status === "queued") {
        toast.success("Submitted for review — your previous phrase stays live.");
      } else {
        toast.success("Catchphrase locked in.");
      }
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail !== null) {
        setError(detail);
        if (detail.banned) {
          toast.error("You've hit the strike limit. Catchphrases are now disabled on this account.");
        } else {
          toast.error(`Inappropriate phrase blocked. Strike ${detail.strikes}/${detail.strike_limit}.`);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return null;

  const banned = data.banned;
  const pending = data.pending;

  return (
    <div
      className="surface-solid p-6"
      data-testid="dashboard-catchphrase-card"
    >
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border-2 border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]">
          <MessageSquareQuote className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-kicker">Your Voice</div>
          <div className="font-heading text-2xl mt-1">Catchphrase</div>
          <div className="text-xs text-white/50 mt-1">
            Pops up over your car when you grab an artifact.
          </div>

          {banned && (
            <div className="mt-4 flex items-center gap-2 px-3 py-2 border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF3B30] text-xs" data-testid="catchphrase-banned">
              <Ban className="w-4 h-4" />
              You hit {data.strike_limit} strikes — catchphrases disabled.
            </div>
          )}

          {!banned && (
            <>
              <div className="mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={50}
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setError(null); }}
                    placeholder="EAT MY EXHAUST"
                    data-testid="catchphrase-input"
                    className="flex-1 bg-black/40 border border-white/10 px-3 py-2 font-heading text-base tracking-wider uppercase focus:border-[#FFCC00] outline-none"
                  />
                  <button
                    onClick={handleSave}
                    disabled={submitting || !draft.trim()}
                    data-testid="catchphrase-save-btn"
                    className={`px-4 py-2 border-2 font-heading text-xs uppercase tracking-widest transition-colors flex items-center gap-1 ${
                      submitting || !draft.trim()
                        ? "border-white/10 text-white/30 cursor-not-allowed"
                        : "border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black"
                    }`}
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-white/40 tracking-wider uppercase">
                  <span>{draft.length}/50</span>
                  {data.strikes > 0 && (
                    <span className="text-[#FF3B30]">
                      Strikes: {data.strikes}/{data.strike_limit}
                    </span>
                  )}
                </div>
              </div>

              {error && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF3B30] text-xs leading-relaxed" data-testid="catchphrase-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    {error.message || "Inappropriate phrase blocked."} {error.banned && "Catchphrases now disabled."}
                  </div>
                </div>
              )}

              {pending && !error && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 border border-[#FFCC00]/50 bg-[#FFCC00]/10 text-[#FFCC00] text-xs" data-testid="catchphrase-pending">
                  <Hourglass className="w-4 h-4" />
                  &quot;{pending}&quot; — under admin review
                </div>
              )}

              {!error && !pending && data.catchphrase && (
                <div className="mt-3 px-3 py-2 border border-white/10 bg-black/40 text-xs text-white/60 italic" data-testid="catchphrase-current">
                  Current: &quot;{data.catchphrase}&quot;
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
